import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  Calendar,
  CheckCircle2,
  Database,
  Filter,
  Gauge,
  Layers3,
  MapPin,
  MousePointerClick,
  ShieldAlert,
  Sparkles,
  Target,
  TrendingUp,
} from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Button } from "@/components/ui/Button";
import { useLanguage } from "@/i18n/LanguageContext";
import type { TranslationKeys } from "@/i18n/translations";

export const AnomalyGuidePage: React.FC<{ publicMode?: boolean }> = ({ publicMode = false }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const anomalyPath = publicMode ? "/embed/anomalies" : "/anomalies";

  const methodCards = useMemo(() => [
    {
      icon: TrendingUp,
      number: "01",
      title: t("guide.method1.title"),
      color: "bg-cyan-50 text-cyan-700 border-cyan-100",
      description: t("guide.method1.desc"),
      detail: t("guide.method1.detail"),
    },
    {
      icon: Activity,
      number: "02",
      title: t("guide.method2.title"),
      color: "bg-amber-50 text-amber-700 border-amber-100",
      description: t("guide.method2.desc"),
      detail: t("guide.method2.detail"),
    },
    {
      icon: Calendar,
      number: "03",
      title: t("guide.method3.title"),
      color: "bg-emerald-50 text-emerald-700 border-emerald-100",
      description: t("guide.method3.desc"),
      detail: t("guide.method3.detail"),
    },
  ], [t]);

  const steps = useMemo(() => [
    { icon: Filter, title: t("guide.step1.title"), text: t("guide.step1.text") },
    { icon: Database, title: t("guide.step2.title"), text: t("guide.step2.text") },
    { icon: Gauge, title: t("guide.step3.title"), text: t("guide.step3.text") },
    { icon: BarChart3, title: t("guide.step4.title"), text: t("guide.step4.text") },
  ], [t]);

  const severityRows = useMemo(() => [
    { label: t("guide.severity.high"), threshold: "|z| >= 2.5", text: t("guide.severity.high.text"), tone: "bg-red-500" },
    { label: t("guide.severity.medium"), threshold: "|z| >= 1.5", text: t("guide.severity.medium.text"), tone: "bg-amber-500" },
    { label: t("guide.severity.normal"), threshold: t("guide.severity.belowThreshold"), text: t("guide.severity.normal.text"), tone: "bg-slate-400" },
  ], [t]);

  const interactionCards = useMemo(() => [
    {
      icon: MousePointerClick,
      title: t("guide.interaction1.title"),
      text: t("guide.interaction1.text"),
      details: [
        t("guide.interaction1.item1"),
        t("guide.interaction1.item2"),
        t("guide.interaction1.item3"),
        t("guide.interaction1.item4"),
      ],
      tone: "border-red-200 bg-red-50/70 text-red-700",
    },
    {
      icon: ShieldAlert,
      title: t("guide.interaction2.title"),
      text: t("guide.interaction2.text"),
      details: [
        t("guide.interaction2.item1"),
        t("guide.interaction2.item2"),
        t("guide.interaction2.item3"),
        t("guide.interaction2.item4"),
      ],
      tone: "border-amber-200 bg-amber-50/70 text-amber-700",
    },
  ], [t]);

  const stats = useMemo(() => [
    { icon: Target, value: "3", label: t("guide.stat.lenses"), text: t("guide.stat.lenses.text") },
    { icon: ShieldAlert, value: "1.5 / 2.5", label: t("guide.stat.gates"), text: t("guide.stat.gates.text") },
    { icon: MapPin, value: "7", label: t("guide.stat.levels"), text: t("guide.stat.levels.text") },
  ], [t]);

  const checklist = useMemo(() => [
    t("guide.checklist.1"),
    t("guide.checklist.2"),
    t("guide.checklist.3"),
    t("guide.checklist.4"),
    t("guide.checklist.5"),
  ], [t]);

  // The score sentence embeds the symbols x / μ / σ, which stay emphasised
  // no matter which language the sentence itself is rendered in.
  const scoreSentence = useMemo(() => {
    const symbolKeys: Record<string, TranslationKeys> = {
      x: "guide.score.symbol.x",
      mu: "guide.score.symbol.mu",
      sigma: "guide.score.symbol.sigma",
    };
    return t("guide.score.desc").split(/(\{\w+\})/g).map((part, index) => {
      const name = part.replace(/[{}]/g, "");
      const key = symbolKeys[name];
      if (!key) return <React.Fragment key={index}>{part}</React.Fragment>;
      return <strong key={index} className="text-slate-700">{t(key)}</strong>;
    });
  }, [t]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar publicMode={publicMode} />
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-9">
        <section className="relative overflow-hidden rounded-[2rem] bg-slate-950 px-6 py-8 text-white shadow-2xl shadow-slate-300/30 sm:px-10 sm:py-11">
          <div className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full border-[28px] border-cyan-400/10" />
          <div className="pointer-events-none absolute bottom-[-5rem] right-1/3 h-48 w-48 rounded-full bg-cyan-400/10 blur-3xl" />
          <div className="relative max-w-3xl">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-cyan-300/20 bg-cyan-300/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-200">
              <Sparkles className="h-3.5 w-3.5" /> {t("guide.badge")}
            </div>
            <h1 className="max-w-2xl text-3xl font-bold tracking-tight sm:text-5xl">{t("guide.title")}</h1>
            <p className="mt-4 max-w-2xl text-sm leading-6 text-slate-300 sm:text-base">
              {t("guide.subtitle")}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button onClick={() => navigate(anomalyPath)} className="bg-cyan-400 text-slate-950 hover:bg-cyan-300">
                <ArrowLeft className="mr-2 h-4 w-4" /> {t("guide.back")}
              </Button>
              <a href="#how-it-works" className="inline-flex items-center rounded-lg border border-white/20 px-4 py-2 text-xs font-semibold text-white transition hover:bg-white/10">
                {t("guide.explore")}
              </a>
            </div>
          </div>
        </section>

        <section className="-mt-5 relative mx-3 grid gap-3 sm:grid-cols-3">
          {stats.map((item) => (
            <div key={item.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-lg shadow-slate-200/50">
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-cyan-50 text-cyan-700"><item.icon className="h-4 w-4" /></div>
                <div><p className="text-xl font-bold text-slate-900">{item.value}</p><p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{item.label}</p><p className="mt-1 text-xs text-slate-500">{item.text}</p></div>
              </div>
            </div>
          ))}
        </section>

        <section id="how-it-works" className="scroll-mt-24 py-14">
          <div className="mb-7 max-w-2xl"><p className="text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-700">{t("guide.workflow.eyebrow")}</p><h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{t("guide.workflow.title")}</h2><p className="mt-3 text-sm leading-6 text-slate-500">{t("guide.workflow.desc")}</p></div>
          <div className="grid gap-3 md:grid-cols-4">
            {steps.map((step, index) => (
              <div key={step.title} className="relative rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-5 flex items-center justify-between"><step.icon className="h-5 w-5 text-cyan-700" /><span className="text-2xl font-black text-slate-100">0{index + 1}</span></div>
                <h3 className="text-sm font-bold text-slate-900">{step.title}</h3><p className="mt-2 text-xs leading-5 text-slate-500">{step.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="methods" className="scroll-mt-24 border-t border-slate-200 py-14">
          <div className="mb-7 flex flex-col justify-between gap-3 sm:flex-row sm:items-end"><div><p className="text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-700">{t("guide.methods.eyebrow")}</p><h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{t("guide.methods.title")}</h2></div><p className="max-w-sm text-xs leading-5 text-slate-500">{t("guide.methods.desc")}</p></div>
          <div className="grid gap-4 lg:grid-cols-3">
            {methodCards.map((method) => (
              <article key={method.title} className={`rounded-2xl border p-5 ${method.color}`}><div className="flex items-center justify-between"><method.icon className="h-5 w-5" /><span className="text-xs font-black opacity-40">{method.number}</span></div><h3 className="mt-7 text-lg font-bold text-slate-900">{method.title}</h3><p className="mt-2 text-sm leading-6 text-slate-700">{method.description}</p><div className="mt-5 border-t border-current/10 pt-4 text-xs leading-5 text-slate-600">{method.detail}</div></article>
            ))}
          </div>
        </section>

        <section id="investigate" className="scroll-mt-24 border-t border-slate-200 py-14">
          <div className="mb-7 max-w-2xl">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-700">{t("guide.investigate.eyebrow")}</p>
            <h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{t("guide.investigate.title")}</h2>
            <p className="mt-3 text-sm leading-6 text-slate-500">{t("guide.investigate.desc")}</p>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            {interactionCards.map((card) => (
              <article key={card.title} className={`rounded-2xl border p-5 ${card.tone}`}>
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/80 shadow-sm"><card.icon className="h-5 w-5" /></div>
                  <div><h3 className="text-base font-bold text-slate-900">{card.title}</h3><p className="mt-2 text-sm leading-6 text-slate-700">{card.text}</p></div>
                </div>
                <div className="mt-5 grid gap-2 border-t border-current/10 pt-4 sm:grid-cols-2">
                  {card.details.map((detail) => <div key={detail} className="flex items-start gap-2 text-xs leading-5 text-slate-600"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />{detail}</div>)}
                </div>
              </article>
            ))}
          </div>
          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start gap-3"><BarChart3 className="mt-0.5 h-5 w-5 shrink-0 text-cyan-700" /><div><h3 className="text-sm font-bold text-slate-900">{t("guide.readDetail.title")}</h3><p className="mt-2 text-xs leading-5 text-slate-500">{t("guide.readDetail.text")}</p></div></div>
          </div>
        </section>

        <section className="grid gap-6 border-t border-slate-200 py-14 lg:grid-cols-[1.1fr_0.9fr]">
          <div><p className="text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-700">{t("guide.score.eyebrow")}</p><h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-900">{t("guide.score.title")}</h2><p className="mt-3 text-sm leading-6 text-slate-500">{scoreSentence}</p><div className="mt-5 rounded-2xl bg-slate-950 p-6 text-center text-xl text-cyan-200 shadow-xl shadow-slate-200 sm:text-2xl">z = <span className="text-white">({t("guide.score.symbol.x")} − {t("guide.score.symbol.mu")}) / {t("guide.score.symbol.sigma")}</span></div><p className="mt-4 text-xs leading-5 text-slate-500">{t("guide.score.footnote")}</p></div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"><div className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" /><h3 className="text-sm font-bold text-slate-900">{t("guide.severity.title")}</h3></div><div className="mt-5 space-y-4">{severityRows.map((row) => <div key={row.label} className="flex gap-3"><span className={`mt-1 h-3 w-3 shrink-0 rounded-full ${row.tone}`} /><div className="min-w-0 flex-1"><div className="flex flex-wrap justify-between gap-2 text-sm font-bold text-slate-800"><span>{row.label}</span><span className="font-mono text-xs text-slate-400">{row.threshold}</span></div><p className="mt-1 text-xs leading-5 text-slate-500">{row.text}</p></div></div>)}</div></div>
        </section>

        <section id="data" className="scroll-mt-24 border-t border-slate-200 py-14"><div className="grid gap-6 lg:grid-cols-3"><div className="lg:col-span-2"><p className="text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-700">{t("guide.data.eyebrow")}</p><h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-900">{t("guide.data.title")}</h2><div className="mt-6 grid gap-3 sm:grid-cols-2"><div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200"><Database className="h-5 w-5 text-cyan-700" /><h3 className="mt-4 text-sm font-bold text-slate-900">{t("guide.data.monthly.title")}</h3><p className="mt-2 text-xs leading-5 text-slate-500">{t("guide.data.monthly.text")}</p></div><div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200"><Layers3 className="h-5 w-5 text-emerald-700" /><h3 className="mt-4 text-sm font-bold text-slate-900">{t("guide.data.hierarchy.title")}</h3><p className="mt-2 text-xs leading-5 text-slate-500">{t("guide.data.hierarchy.text")}</p></div></div></div><aside className="rounded-2xl border border-cyan-100 bg-cyan-50/70 p-5"><MapPin className="h-5 w-5 text-cyan-700" /><h3 className="mt-4 text-sm font-bold text-slate-900">{t("guide.checklist.title")}</h3><ul className="mt-4 space-y-3 text-xs leading-5 text-slate-600">{checklist.map((item) => <li key={item} className="flex gap-2"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-cyan-700" />{item}</li>)}</ul></aside></div></section>

        <section className="rounded-2xl border border-amber-200 bg-amber-50/70 p-5 sm:p-6"><div className="flex items-start gap-3"><AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" /><div><h2 className="text-sm font-bold text-slate-900">{t("guide.note.title")}</h2><p className="mt-2 max-w-4xl text-xs leading-5 text-slate-700">{t("guide.note.text")}</p></div></div></section>
      </main>
    </div>
  );
};
